/**
 * GRABOT Theme - Global JavaScript
 * Handles: sticky header, scroll-reveal, mobile menu, platform carousel, about carousel
 */

(function() {
  'use strict';

  function publishGrabotEvent(eventName, eventData, attempt = 0) {
    const analytics = window.Shopify?.analytics;
    if (!analytics || typeof analytics.publish !== 'function') {
      if (attempt < 20) {
        window.setTimeout(() => publishGrabotEvent(eventName, eventData, attempt + 1), 250);
      }
      return false;
    }

    try {
      analytics.publish(eventName, {
        ...eventData,
        page_location: window.location.href,
        page_path: window.location.pathname
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  // ---- Sticky Header ----
  const header = document.getElementById('site-header');
  if (header) {
    let headerTicking = false;
    const updateHeaderState = () => {
      header.classList.toggle('scrolled', window.scrollY > 10);
      headerTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!headerTicking) {
        headerTicking = true;
        requestAnimationFrame(updateHeaderState);
      }
    }, { passive: true });
    updateHeaderState();
  }

  // ---- Mobile Menu Toggle ----
  const mobileMenuBtn = document.getElementById('mobile-menu-btn');
  const mobileDrawer = document.getElementById('mobile-menu-drawer');
  if (mobileMenuBtn && mobileDrawer) {
    const closeMobileMenu = () => {
      mobileDrawer.classList.remove('open');
      mobileMenuBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    };

    mobileMenuBtn.addEventListener('click', () => {
      const isOpen = mobileDrawer.classList.toggle('open');
      mobileMenuBtn.setAttribute('aria-expanded', String(isOpen));
      document.body.classList.toggle('menu-open', isOpen);
    });
    // Close drawer when a link is clicked
    mobileDrawer.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        closeMobileMenu();
      });
    });
    // Close drawer when clicking the overlay background
    mobileDrawer.addEventListener('click', (e) => {
      if (e.target === mobileDrawer) {
        closeMobileMenu();
      }
    });

    document.addEventListener('keydown', event => {
      if (!mobileDrawer.classList.contains('open')) return;

      if (event.key === 'Escape') {
        closeMobileMenu();
        mobileMenuBtn.focus();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(mobileDrawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter(element => !element.hasAttribute('hidden'));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  // ---- Contact Form Feedback and Submission ----
  function focusFormFeedback(feedback) {
    if (!feedback) return;
    window.setTimeout(() => {
      feedback.focus({ preventScroll: true });
      feedback.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }

  const contactForm = document.getElementById('contact_form');
  if (contactForm && contactForm.dataset.grabotContactInitialized !== 'true') {
    contactForm.dataset.grabotContactInitialized = 'true';
    const submitButton = contactForm.querySelector('[type="submit"]');
    const originalButtonHtml = submitButton?.innerHTML || '';
    const startSessionKey = 'grabot:contact_form_start:published';
    const nativePendingKey = 'grabot:contact_form_native_pending';
    const formEventData = {
      form_id: 'contact_form',
      form_name: 'contact',
      form_destination: '/contact',
      lead_source: 'contact_form'
    };
    let startPublished = false;

    try {
      startPublished = window.sessionStorage.getItem(startSessionKey) === 'true';
    } catch (error) {
      startPublished = false;
    }

    const publishFormStart = event => {
      if (startPublished || !event.target.matches('input:not([type="hidden"]), select, textarea')) return;
      startPublished = true;
      try {
        window.sessionStorage.setItem(startSessionKey, 'true');
      } catch (error) {
        // Session storage can be unavailable in privacy-restricted browsers.
      }
      publishGrabotEvent('grabot:contact_form_start', formEventData);
    };

    const setSubmitting = submitting => {
      if (submitting) {
        contactForm.setAttribute('aria-busy', 'true');
      } else {
        contactForm.removeAttribute('aria-busy');
      }
      contactForm.dataset.contactSubmitting = submitting ? 'true' : 'false';
      if (!submitButton) return;

      submitButton.disabled = submitting;
      submitButton.classList.toggle('is-loading', submitting);
      if (submitting) {
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = submitButton.dataset.loadingText || 'Sending…';
      } else {
        submitButton.removeAttribute('aria-busy');
        submitButton.innerHTML = originalButtonHtml;
      }
    };

    const setNativePending = pending => {
      try {
        if (pending) {
          window.sessionStorage.setItem(nativePendingKey, 'true');
        } else {
          window.sessionStorage.removeItem(nativePendingKey);
        }
      } catch (error) {
        // Session storage can be unavailable in privacy-restricted browsers.
      }
    };

    const publishNativeSuccess = () => {
      if (!contactForm.querySelector('[data-contact-form-success]')) return;

      let nativePending = false;
      try {
        nativePending = window.sessionStorage.getItem(nativePendingKey) === 'true';
      } catch (error) {
        nativePending = false;
      }

      if (!nativePending) return;
      setNativePending(false);
      publishGrabotEvent('grabot:contact_form_success', formEventData);
    };

    contactForm.addEventListener('input', publishFormStart);
    contactForm.addEventListener('change', publishFormStart);
    contactForm.addEventListener('submit', event => {
      if (!contactForm.checkValidity()) {
        return;
      }

      setNativePending(true);
      setSubmitting(true);
      publishGrabotEvent('grabot:contact_form_submit', formEventData);
    }, true);

    publishNativeSuccess();
  }

  focusFormFeedback(document.querySelector('[data-form-feedback]'));

  // ---- Scroll-Reveal Animation ----
  const revealElements = document.querySelectorAll('.reveal');
  if (revealElements.length > 0) {
    const observerOptions = { threshold: 0.15, rootMargin: '0px 0px -40px 0px' };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    revealElements.forEach(el => observer.observe(el));
  }

  // ---- Generic Carousel ----
  // Used by platform-carousel and about-carousel sections
  document.querySelectorAll('[data-carousel]').forEach(carousel => {
    const track = carousel.querySelector('[data-carousel-track]');
    const slides = carousel.querySelectorAll('[data-carousel-slide]');
    const dots = carousel.querySelectorAll('[data-carousel-dot]');
    const prevBtn = carousel.querySelector('[data-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-carousel-next]');
    if (!slides.length) return;

    let current = 0;
    const total = slides.length;

    function goTo(idx) {
      current = (idx + total) % total;

      // If using CSS class-based slides (like platform carousel)
      if (!track) {
        slides.forEach((s, i) => s.classList.toggle('active', i === current));
      } else {
        // Transform-based (like about carousel)
        track.style.transform = `translateX(-${current * 100}%)`;
      }

      dots.forEach((d, i) => d.classList.toggle('active', i === current));
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));
    dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));

    // Auto-play
    const interval = parseInt(carousel.dataset.carouselInterval) || 5000;
    let autoplay = setInterval(() => goTo(current + 1), interval);
    carousel.addEventListener('mouseenter', () => clearInterval(autoplay));
    carousel.addEventListener('mouseleave', () => {
      autoplay = setInterval(() => goTo(current + 1), interval);
    });
  });

  // ---- FAQ Accordion ----
  document.querySelectorAll('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.parentElement;
      const isOpen = item.classList.contains('open');
      // Close all
      document.querySelectorAll('.faq-item.open').forEach(openItem => {
        openItem.classList.remove('open');
        openItem.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
      });
      // Toggle clicked
      if (!isOpen) {
        item.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // ---- FAQ Category Filter ----
  document.querySelectorAll('.faq-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.faq-filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const cat = btn.dataset.category;
      document.querySelectorAll('.faq-item').forEach(item => {
        item.style.display = (cat === 'all' || item.dataset.cat === cat) ? '' : 'none';
      });
    });
  });

  // ---- Exhibitions Page Image Switcher ----
  document.querySelectorAll('[data-exhibitions-hero]').forEach(section => {
    const tabs = section.querySelectorAll('.exhibition-nav-item');
    const panels = section.querySelectorAll('.exhibition-panel');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetId = tab.dataset.exhibitionTarget;
        tabs.forEach(item => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach(panel => {
          panel.classList.toggle('is-active', panel.id === targetId);
        });
      });
    });
  });

  // ---- Lazy Media ----
  const lazyVideos = document.querySelectorAll('video[data-lazy-video]');
  if (lazyVideos.length > 0) {
    let videoObserverStarted = false;
    const loadVideo = video => {
      if (video.dataset.loaded === 'true') return;
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
      if (video.hasAttribute('autoplay')) {
        video.play().catch(() => {});
      }
    };

    const startVideoObserver = () => {
      if (videoObserverStarted) return;
      videoObserverStarted = true;

      if (!('IntersectionObserver' in window)) {
        lazyVideos.forEach(loadVideo);
        return;
      }

      const videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            loadVideo(entry.target);
            videoObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '150px 0px', threshold: 0 });
      lazyVideos.forEach(video => videoObserver.observe(video));
    };

    document.querySelectorAll('a[href="#video"], a[href$="#video"]').forEach(link => {
      link.addEventListener('click', startVideoObserver, { once: true });
    });
    window.addEventListener('scroll', startVideoObserver, { once: true, passive: true });
    window.addEventListener('touchstart', startVideoObserver, { once: true, passive: true });
    window.setTimeout(startVideoObserver, 8000);
  }

  // ---- Floating Contact Widget and Shopify Inbox ----
  const inboxHostSelector = [
    '#ShopifyChat',
    '#shopify-chat',
    'shopify-chat',
    'inbox-online-store-chat',
    'shopify-agent[view="chat"]',
    'shopify-agent-runtime'
  ].join(',');
  const inboxLauncherSelector = [
    '#dummy-chat-button',
    'button.chat-toggle',
    'button.activator',
    '[data-testid="chat-toggle"]',
    'button[aria-controls*="chat" i]',
    'button[aria-label*="chat" i]',
    '[role="button"][aria-label*="chat" i]'
  ].join(',');
  let managedShopifyInboxLauncher = null;
  let inboxRuntimePrewarmStarted = false;
  let inboxUpdateFrame = 0;
  const observedInboxSurfaces = new WeakSet();
  const inboxValueWaiters = new Set();

  function getShopifyInboxParts() {
    const hosts = [];
    const roots = [document];
    const seenHosts = new Set();
    const seenRoots = new Set([document]);

    for (let index = 0; index < roots.length; index += 1) {
      const root = roots[index];
      root.querySelectorAll(inboxHostSelector).forEach(host => {
        if (seenHosts.has(host)) return;
        seenHosts.add(host);
        hosts.push(host);
        if (host.shadowRoot && !seenRoots.has(host.shadowRoot)) {
          seenRoots.add(host.shadowRoot);
          roots.push(host.shadowRoot);
        }
      });
    }

    return { hosts, roots };
  }

  function isVisibleElement(element) {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  function isShopifyInboxOpen() {
    const { hosts, roots } = getShopifyInboxParts();
    if (!hosts.length) return false;

    if (hosts.some(host => (
      host.getAttribute('is-open') === 'true' ||
      host.getAttribute('data-state') === 'open' ||
      host.hasAttribute('open') ||
      host.classList.contains('is-open')
    ))) return true;

    return roots.some(root => {
      const openElement = root.querySelector([
        'dialog[open]',
        '[role="dialog"]:not([hidden])',
        '[aria-modal="true"]:not([hidden])',
        '[data-chat-open="true"]',
        'iframe[id*="chat-window" i]',
        'iframe[title*="chat window" i]'
      ].join(','));
      return isVisibleElement(openElement);
    });
  }

  function getShopifyInboxController() {
    const { hosts } = getShopifyInboxParts();
    return hosts.find(host => (
      typeof host.show === 'function' &&
      typeof host.close === 'function'
    )) || null;
  }

  function findShopifyInboxLauncher() {
    const { roots } = getShopifyInboxParts();
    for (const root of roots) {
      const candidates = Array.from(root.querySelectorAll(inboxLauncherSelector));
      const eligibleCandidates = candidates.filter(candidate => (
        !candidate.closest('[data-floating-contact]') &&
        !candidate.matches('[aria-label*="close" i]')
      ));
      const launcher = eligibleCandidates.find(isVisibleElement) || eligibleCandidates[0];
      if (launcher) return launcher;
    }
    return null;
  }

  function getShopifyInboxLauncher() {
    if (managedShopifyInboxLauncher?.isConnected) return managedShopifyInboxLauncher;
    managedShopifyInboxLauncher = findShopifyInboxLauncher();
    return managedShopifyInboxLauncher;
  }

  function hideShopifyInboxLauncher(knownOpen = isShopifyInboxOpen()) {
    if (knownOpen) return false;
    const launcher = getShopifyInboxLauncher();
    if (!launcher) return false;
    if (launcher.dataset.grabotLauncherHidden === 'true') return true;

    // Shopify Inbox renders its current launcher inside an open Shadow DOM. Keep
    // it available for the custom Live Chat trigger, but move the duplicate UI
    // completely out of the viewport while the native panel is closed.
    launcher.style.setProperty('transform', 'translateX(calc(100vw + 12rem))', 'important');
    launcher.style.setProperty('pointer-events', 'none', 'important');
    launcher.setAttribute('data-grabot-launcher-hidden', 'true');
    return true;
  }

  function createInboxAttemptId() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function getInboxEventContext() {
    return {
      entry_point: 'contact_widget',
      market: window.Shopify?.country || 'unknown',
      prewarm_started: inboxRuntimePrewarmStarted
    };
  }

  function prewarmShopifyInboxRuntime() {
    if (inboxRuntimePrewarmStarted) return true;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return false;

    inboxRuntimePrewarmStarted = true;
    const runtimeUrl = 'https://cdn.shopify.com/storefront/web-components/agent-iframe.js';
    if (!document.querySelector(`link[data-grabot-inbox-prewarm][href="${runtimeUrl}"]`)) {
      const preload = document.createElement('link');
      preload.rel = 'modulepreload';
      preload.href = runtimeUrl;
      preload.crossOrigin = 'anonymous';
      preload.dataset.grabotInboxPrewarm = 'true';
      preload.addEventListener('error', () => {
        inboxRuntimePrewarmStarted = false;
        preload.remove();
      }, { once: true });
      document.head.appendChild(preload);
    }
    return true;
  }

  const inboxOpenTracker = window.__grabotInboxOpenTracker || {
    initialized: false,
    open: false,
    recordedForCurrentOpen: false,
    pendingEntryPoint: '',
    pendingAt: 0
  };
  window.__grabotInboxOpenTracker = inboxOpenTracker;

  function setPendingInboxEntry(entryPoint) {
    inboxOpenTracker.pendingEntryPoint = entryPoint;
    inboxOpenTracker.pendingAt = Date.now();
  }

  function clearPendingInboxEntry(entryPoint) {
    if (!entryPoint || inboxOpenTracker.pendingEntryPoint === entryPoint) {
      inboxOpenTracker.pendingEntryPoint = '';
      inboxOpenTracker.pendingAt = 0;
    }
  }

  function recordInboxOpenIfNeeded(entryPoint, knownOpen = isShopifyInboxOpen()) {
    if (inboxOpenTracker.recordedForCurrentOpen || !knownOpen) return;
    inboxOpenTracker.recordedForCurrentOpen = true;
    inboxOpenTracker.open = true;
    publishGrabotEvent('grabot:live_chat_open', {
      entry_point: entryPoint || 'shopify_inbox'
    });
    clearPendingInboxEntry();
  }

  function updateInboxOpenState() {
    const { hosts } = getShopifyInboxParts();
    if (!hosts.length) return;
    const open = isShopifyInboxOpen();
    document.querySelectorAll('[data-shopify-inbox-trigger]').forEach(trigger => {
      trigger.setAttribute('aria-pressed', open ? 'true' : 'false');
      trigger.dataset.chatState = open ? 'open' : 'closed';
    });
    if (!open) hideShopifyInboxLauncher(false);
    const hasFreshPendingEntry = inboxOpenTracker.pendingEntryPoint && Date.now() - inboxOpenTracker.pendingAt < 8000;

    if (!inboxOpenTracker.initialized) {
      inboxOpenTracker.initialized = true;
      inboxOpenTracker.open = open;
      inboxOpenTracker.recordedForCurrentOpen = open && !hasFreshPendingEntry;
      if (open && hasFreshPendingEntry) recordInboxOpenIfNeeded(inboxOpenTracker.pendingEntryPoint, true);
      return;
    }

    if (open && !inboxOpenTracker.open) {
      inboxOpenTracker.recordedForCurrentOpen = false;
      recordInboxOpenIfNeeded(hasFreshPendingEntry ? inboxOpenTracker.pendingEntryPoint : 'shopify_inbox', true);
    } else if (!open && inboxOpenTracker.open) {
      inboxOpenTracker.recordedForCurrentOpen = false;
      clearPendingInboxEntry();
    }

    if (!open && inboxOpenTracker.pendingEntryPoint && Date.now() - inboxOpenTracker.pendingAt >= 8000) {
      clearPendingInboxEntry();
    }
    inboxOpenTracker.open = open;
  }

  function resolveInboxValueWaiters() {
    inboxValueWaiters.forEach(check => check());
  }

  function observeInboxSurface(surface, options, observer) {
    if (!surface || observedInboxSurfaces.has(surface)) return;
    observedInboxSurfaces.add(surface);
    observer.observe(surface, options);
  }

  function observeDiscoveredInboxSurfaces(observer) {
    const { hosts, roots } = getShopifyInboxParts();
    hosts.forEach(host => observeInboxSurface(host, {
      attributes: true,
      attributeFilter: ['is-open', 'open', 'data-state', 'class']
    }, observer));
    roots.forEach(root => {
      if (root === document) return;
      observeInboxSurface(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['is-open', 'open', 'data-state', 'aria-hidden', 'class']
      }, observer);
    });
  }

  function scheduleInboxStateUpdate() {
    if (inboxUpdateFrame) return;
    inboxUpdateFrame = window.requestAnimationFrame(() => {
      inboxUpdateFrame = 0;
      observeDiscoveredInboxSurfaces(inboxStateObserver);
      updateInboxOpenState();
      resolveInboxValueWaiters();
    });
  }

  const inboxStateObserver = new MutationObserver(scheduleInboxStateUpdate);
  observeInboxSurface(document, { childList: true, subtree: true }, inboxStateObserver);

  function waitForInboxValue(getValue, timeout) {
    return new Promise(resolve => {
      let settled = false;
      let fallbackTimer = 0;
      let timeoutTimer = 0;

      const finish = value => {
        if (settled) return;
        settled = true;
        inboxValueWaiters.delete(check);
        window.clearTimeout(fallbackTimer);
        window.clearTimeout(timeoutTimer);
        resolve(value || null);
      };

      const check = () => {
        const value = getValue();
        if (value) finish(value);
      };

      const fallbackCheck = () => {
        check();
        if (!settled) fallbackTimer = window.setTimeout(fallbackCheck, 500);
      };

      inboxValueWaiters.add(check);
      timeoutTimer = window.setTimeout(() => finish(null), timeout);
      check();
      if (!settled) fallbackTimer = window.setTimeout(fallbackCheck, 500);
    });
  }

  async function openShopifyInboxFromWidget() {
    const startedAt = performance.now();
    const attemptId = createInboxAttemptId();
    const eventContext = getInboxEventContext();
    const publishResult = (success, reason, launcherReadyAt = startedAt) => {
      publishGrabotEvent('grabot:inbox_open_result', {
        ...eventContext,
        attempt_id: attemptId,
        success,
        reason,
        duration_ms: Math.round(performance.now() - startedAt),
        launcher_wait_ms: Math.round(launcherReadyAt - startedAt)
      });
    };

    publishGrabotEvent('grabot:live_chat_click', {
      ...eventContext,
      attempt_id: attemptId
    });

    if (isShopifyInboxOpen()) {
      publishResult(true, 'already_open');
      return true;
    }

    prewarmShopifyInboxRuntime();
    const controller = getShopifyInboxController() || await waitForInboxValue(getShopifyInboxController, 2000);
    const controlReadyAt = performance.now();
    const launcher = controller ? null : (getShopifyInboxLauncher() || await waitForInboxValue(getShopifyInboxLauncher, 1000));
    if (!controller && !launcher) {
      publishResult(false, 'chat_control_unavailable', controlReadyAt);
      return false;
    }

    setPendingInboxEntry('contact_widget');
    try {
      if (controller) {
        controller.show();
      } else {
        launcher.click();
      }
    } catch {
      clearPendingInboxEntry('contact_widget');
      publishResult(false, 'open_control_failed', controlReadyAt);
      return false;
    }
    const remainingTimeout = Math.max(1000, 5000 - (performance.now() - startedAt));
    const opened = await waitForInboxValue(isShopifyInboxOpen, remainingTimeout);
    if (!opened) {
      clearPendingInboxEntry('contact_widget');
      publishResult(false, 'open_timeout', controlReadyAt);
      return false;
    }
    recordInboxOpenIfNeeded('contact_widget', true);
    publishResult(true, controller ? 'opened_via_api' : 'opened_via_launcher', controlReadyAt);
    return true;
  }

  async function closeShopifyInboxFromWidget() {
    const startedAt = performance.now();
    if (!isShopifyInboxOpen()) return true;

    const controller = getShopifyInboxController() || await waitForInboxValue(getShopifyInboxController, 1000);
    if (!controller) return false;

    try {
      controller.close();
    } catch {
      return false;
    }
    const closed = await waitForInboxValue(() => !isShopifyInboxOpen(), 3000);
    if (!closed) return false;

    inboxOpenTracker.open = false;
    inboxOpenTracker.recordedForCurrentOpen = false;
    clearPendingInboxEntry();
    hideShopifyInboxLauncher(false);
    publishGrabotEvent('grabot:live_chat_close', {
      entry_point: 'contact_widget',
      duration_ms: Math.round(performance.now() - startedAt)
    });
    scheduleInboxStateUpdate();
    return true;
  }

  scheduleInboxStateUpdate();
  [250, 1000, 3000].forEach(delay => window.setTimeout(scheduleInboxStateUpdate, delay));

  document.querySelectorAll('[data-floating-contact]').forEach(widget => {
    if (widget.dataset.grabotContactWidgetInitialized === 'true') return;
    widget.dataset.grabotContactWidgetInitialized = 'true';
    const toggle = widget.querySelector('.floating-contact-toggle');
    const panel = widget.querySelector('.floating-contact-panel');
    const prompt = widget.querySelector('[data-floating-contact-prompt]');
    const inboxTrigger = widget.querySelector('[data-shopify-inbox-trigger]');
    const status = widget.querySelector('[data-floating-contact-status]');
    if (!toggle || !panel) return;

    function setOpen(open) {
      widget.classList.toggle('is-open', open);
      if (open) widget.classList.remove('is-prompt-visible');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    function dismissPrompt() {
      widget.classList.remove('is-prompt-visible');
    }

    function setStatus(message) {
      if (status) status.textContent = message || '';
    }

    function setChatUnavailable(message) {
      if (!inboxTrigger) return;
      widget.classList.add('has-chat-fallback');
      inboxTrigger.disabled = false;
      inboxTrigger.removeAttribute('aria-disabled');
      setStatus(message || inboxTrigger.dataset.chatUnavailableMessage);
      setOpen(true);
    }

    function clearChatUnavailable() {
      if (!inboxTrigger) return;
      widget.classList.remove('has-chat-fallback');
      inboxTrigger.disabled = false;
      inboxTrigger.removeAttribute('aria-disabled');
      setStatus('');
    }

    toggle.addEventListener('click', event => {
      event.stopPropagation();
      dismissPrompt();
      setOpen(!widget.classList.contains('is-open'));
    });

    if (prompt) {
      window.setTimeout(() => {
        if (!isShopifyInboxOpen() && !widget.classList.contains('is-open')) {
          widget.classList.add('is-prompt-visible');
        }
      }, 350);

      prompt.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        dismissPrompt();
        if (inboxTrigger) {
          inboxTrigger.click();
        } else {
          setOpen(true);
        }
      });
    }

    document.addEventListener('click', event => {
      if (!widget.contains(event.target)) setOpen(false);
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') setOpen(false);
    });

    if (inboxTrigger) {
      inboxTrigger.setAttribute('aria-pressed', isShopifyInboxOpen() ? 'true' : 'false');
      const prewarmInboxFromIntent = () => prewarmShopifyInboxRuntime();
      widget.addEventListener('pointerenter', prewarmInboxFromIntent, { once: true, passive: true });
      widget.addEventListener('focusin', prewarmInboxFromIntent, { once: true });
      widget.addEventListener('touchstart', prewarmInboxFromIntent, { once: true, passive: true });

      inboxTrigger.addEventListener('click', async event => {
        event.preventDefault();
        event.stopPropagation();
        dismissPrompt();
        if (inboxTrigger.getAttribute('aria-busy') === 'true') return;

        const shouldClose = isShopifyInboxOpen();
        clearChatUnavailable();
        setStatus(shouldClose ? 'Closing live chat…' : (inboxTrigger.dataset.chatConnectingMessage || 'Connecting to live chat…'));
        inboxTrigger.classList.add('is-loading');
        inboxTrigger.setAttribute('aria-busy', 'true');

        const actionSucceeded = shouldClose
          ? await closeShopifyInboxFromWidget()
          : await openShopifyInboxFromWidget();
        inboxTrigger.classList.remove('is-loading');
        inboxTrigger.removeAttribute('aria-busy');

        if (actionSucceeded) {
          clearChatUnavailable();
          setOpen(false);
        } else if (shouldClose) {
          setStatus('Live chat could not be closed. Please try again.');
          setOpen(true);
        } else {
          setChatUnavailable(inboxTrigger.dataset.chatUnavailableMessage);
        }
      });
    }

    document.addEventListener('focusin', event => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (event.target.matches('input, select, textarea')) widget.classList.add('is-input-focused');
    });

    document.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!document.activeElement?.matches('input, select, textarea')) {
          widget.classList.remove('is-input-focused');
        }
      }, 0);
    });

    const contactSubmit = document.getElementById('contact-submit-btn');
    if (contactSubmit && 'IntersectionObserver' in window) {
      const actionObserver = new IntersectionObserver(entries => {
        widget.classList.toggle('is-primary-action-visible', entries.some(entry => entry.isIntersecting));
      }, { threshold: 0.2 });
      actionObserver.observe(contactSubmit);
    }
  });

  // ---- PDP Thumbnail Gallery ----
  function normalizeShopifyImageUrl(url) {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.origin);
      parsedUrl.searchParams.delete('width');
      const query = parsedUrl.searchParams.toString();
      return `${parsedUrl.pathname}${query ? '?' + query : ''}`;
    } catch (error) {
      return url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    }
  }

  function setActivePdpThumb(gallery, activeThumb) {
    gallery.querySelectorAll('[data-pdp-thumb]').forEach(item => {
      const active = item === activeThumb;
      item.classList.toggle('active', active);
      if (active) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  function syncActivePdpThumb(gallery, mainImg) {
    const mainImageKey = normalizeShopifyImageUrl(mainImg.currentSrc || mainImg.getAttribute('src'));
    if (!mainImageKey) return;

    const activeThumb = Array.from(gallery.querySelectorAll('[data-pdp-thumb]')).find(thumb => (
      normalizeShopifyImageUrl(thumb.dataset.src) === mainImageKey ||
      normalizeShopifyImageUrl(thumb.querySelector('img')?.src) === mainImageKey
    ));

    if (activeThumb) setActivePdpThumb(gallery, activeThumb);
  }

  function initPdpGalleries(root = document) {
    root.querySelectorAll('[data-pdp-gallery]').forEach(gallery => {
      if (gallery.dataset.pdpGalleryBound === 'true') return;
      gallery.dataset.pdpGalleryBound = 'true';

      const mainImg = gallery.querySelector('[data-pdp-main-image]');
      if (!mainImg) return;

      syncActivePdpThumb(gallery, mainImg);

      gallery.addEventListener('click', event => {
        const thumb = event.target.closest('[data-pdp-thumb]');
        if (!thumb || !gallery.contains(thumb)) return;

        const nextSrc = thumb.dataset.src;
        if (!nextSrc) return;

        event.preventDefault();

        setActivePdpThumb(gallery, thumb);

        mainImg.src = nextSrc;
        if (thumb.dataset.srcset) {
          mainImg.setAttribute('srcset', thumb.dataset.srcset);
        } else {
          mainImg.removeAttribute('srcset');
        }
        mainImg.alt = thumb.dataset.alt || thumb.querySelector('img')?.alt || '';
      });
    });
  }

  initPdpGalleries();
  document.addEventListener('shopify:section:load', event => initPdpGalleries(event.target));

  // ---- PDP Tabs ----
  function initPdpTabs(root = document) {
    root.querySelectorAll('.pdp-tabs, .techspecs-tabs').forEach(tabList => {
      if (tabList.dataset.tabsBound === 'true') return;
      tabList.dataset.tabsBound = 'true';

      const tabs = Array.from(tabList.querySelectorAll('.pdp-tab, .techspecs-tab'));
      const section = tabList.closest('section');
      if (!tabs.length || !section) return;

      const activateTab = tab => {
        const targetId = tab.getAttribute('aria-controls');
        const target = targetId ? section.querySelector('#' + targetId) : null;
        if (!target) return;

        tabs.forEach(item => {
          const isActive = item === tab;
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-selected', String(isActive));
          item.tabIndex = isActive ? 0 : -1;
        });

        const panels = section.querySelectorAll('.pdp-tab-content, .techspecs-tab-content');
        panels.forEach(panel => {
          const isActive = panel === target;
          panel.classList.toggle('active', isActive);
          panel.hidden = !isActive;
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(tab));
        tab.addEventListener('keydown', event => {
          let nextIndex = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === null) return;

          event.preventDefault();
          tabs[nextIndex].focus();
          activateTab(tabs[nextIndex]);
        });
      });
    });
  }

  function initSpecsDisclosures(root = document) {
    root.querySelectorAll('.pdp-details .pdp-specs-table, .pdp-techspecs .pdp-specs-table').forEach((table, index) => {
      if (table.dataset.specsDisclosureBound === 'true' || table.rows.length <= 6) return;
      table.dataset.specsDisclosureBound = 'true';

      if (!table.id) table.id = 'pdp-specs-' + index + '-' + Date.now();
      table.classList.add('is-mobile-collapsed');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pdp-specs-more-toggle';
      button.setAttribute('aria-controls', table.id);
      button.setAttribute('aria-expanded', 'false');
      button.textContent = `Show all ${table.rows.length} specifications`;
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        table.classList.toggle('is-mobile-collapsed', expanded);
        button.textContent = expanded ? `Show all ${table.rows.length} specifications` : 'Show fewer specifications';
      });
      table.insertAdjacentElement('afterend', button);
    });
  }

  initPdpTabs();
  initSpecsDisclosures();
  document.addEventListener('shopify:section:load', event => {
    initPdpTabs(event.target);
    initSpecsDisclosures(event.target);
  });

  // ---- Article TOC Auto-generation ----
  const articleBody = document.getElementById('article-body');
  const tocList = document.getElementById('toc-list');
  if (articleBody && tocList) {
    const headings = articleBody.querySelectorAll('h2');
    headings.forEach((h, i) => {
      // Auto-generate id if missing (Shopify editor strips ids)
      if (!h.id) {
        h.id = h.textContent.trim().toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') || ('section-' + i);
      }
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      tocList.appendChild(li);
    });

    // Smooth scroll
    tocList.addEventListener('click', (e) => {
      if (e.target.tagName === 'A') {
        e.preventDefault();
        const target = document.querySelector(e.target.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          history.replaceState(null, '', e.target.getAttribute('href'));
        }
      }
    });

    // Active state via IntersectionObserver
    const tocLinks = tocList.querySelectorAll('a');
    const tocObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          tocLinks.forEach(l => l.classList.remove('is-active'));
          const activeLink = tocList.querySelector('a[href="#' + entry.target.id + '"]');
          if (activeLink) activeLink.classList.add('is-active');
        }
      });
    }, {
      rootMargin: '-100px 0px -60% 0px',
      threshold: 0
    });

    headings.forEach(h => { if (h.id) tocObserver.observe(h); });
  }

  // ---- Nav Dropdown ----
  const navDropdowns = document.querySelectorAll('.nav-has-dropdown');
  navDropdowns.forEach(li => {
    const dropdown = li.querySelector('.nav-dropdown');
    if (!dropdown) return;
    const trigger = Array.from(li.children).find(child => child.matches && child.matches('a'));
    let hideTimer;
    const setOpen = (isOpen) => {
      dropdown.classList.toggle('open', isOpen);
      if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
    };
    li.addEventListener('mouseenter', () => {
      clearTimeout(hideTimer);
      setOpen(true);
    });
    li.addEventListener('mouseleave', () => {
      hideTimer = setTimeout(() => setOpen(false), 150);
    });
    li.addEventListener('focusin', () => {
      clearTimeout(hideTimer);
      setOpen(true);
    });
    li.addEventListener('focusout', (event) => {
      if (!li.contains(event.relatedTarget)) {
        setOpen(false);
      }
    });
  });

  // Product mega menu: switch the independent level-3 surface when a
  // level-2 category receives pointer or keyboard focus.
  document.querySelectorAll('[data-product-mega]').forEach(menu => {
    const categoryLinks = menu.querySelectorAll('[data-product-target]');
    const panels = menu.querySelectorAll('[data-product-panel]');
    if (!categoryLinks.length || !panels.length) return;

    const activateCategory = (target) => {
      categoryLinks.forEach(link => {
        const active = link.dataset.productTarget === target;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
      });
      panels.forEach(panel => {
        const active = panel.dataset.productPanel === target;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };

    categoryLinks.forEach(link => {
      link.addEventListener('mouseenter', () => activateCategory(link.dataset.productTarget));
      link.addEventListener('focusin', () => activateCategory(link.dataset.productTarget));
    });
  });

})();
